import type { Lesson } from './courses';

/**
 * Parcours 3 : SEO & Referencement — 6 lecons.
 */
export const SEO_REFERENCEMENT_LESSONS: Lesson[] = [
  {
    slug: 'seo-basics',
    title: 'Les fondamentaux du SEO',
    duration: '4 min',
    glossaryTerms: ['seo', 'blog-manager'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le SEO (Search Engine Optimization) regroupe l'ensemble des techniques qui permettent d'ameliorer la visibilite d'un site web dans les resultats des moteurs de recherche comme Google. L'objectif : apparaitre dans les premiers resultats quand un prospect tape une requete liee a votre activite.\n\nLe SEO repose sur 3 piliers :\n- **Technique** — Vitesse du site, structure des URLs, balisage\n- **Contenu** — Articles de qualite qui repondent aux questions des utilisateurs\n- **Popularite** — Liens entrants (backlinks) depuis d'autres sites",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le trafic organique (SEO) represente en moyenne 53% du trafic total d'un site B2B (BrightEdge). Contrairement a la publicite payante qui s'arrete quand vous coupez le budget, un article bien positionne genere du trafic pendant des mois, voire des annees.\n\n75% des internautes ne depassent jamais la premiere page de Google. Etre en page 2, c'est presque comme etre invisible.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez vos mots-cles cibles** — Quels termes vos prospects recherchent-ils ?\n2. **Creez du contenu de qualite** — Un article par mot-cle, exhaustif et utile\n3. **Optimisez vos balises** — Title, meta description, H1, H2 pour chaque page\n4. **Ameliorez la technique** — Vitesse de chargement, responsive mobile, HTTPS\n5. **Construisez des liens** — Guest posts, partenariats, relations presse",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Blog Manager** integre tous les champs SEO essentiels : meta title, meta description, slug personnalisable, image a la une et excerpt. Chaque article publie est automatiquement optimise pour le referencement.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
  {
    slug: 'keyword-research',
    title: 'Recherche de mots-cles',
    duration: '4 min',
    glossaryTerms: ['seo', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La recherche de mots-cles (Keyword Research) consiste a identifier les termes que vos prospects utilisent dans les moteurs de recherche. C'est la fondation de toute strategie SEO.\n\nUn bon mot-cle allie 3 criteres :\n- **Volume** — Suffisamment de recherches mensuelles (au moins 100-500)\n- **Pertinence** — Directement lie a votre activite et votre offre\n- **Faisabilite** — Concurrence raisonnable pour pouvoir se positionner",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Rediger un article sans recherche de mots-cles, c'est comme ouvrir un magasin sans savoir si des clients passent dans la rue. Vous risquez de produire du contenu que personne ne recherche.\n\nA l'inverse, cibler un mot-cle tres concurrentiel quand votre site est jeune est voue a l'echec. La recherche de mots-cles vous aide a trouver le bon equilibre : des requetes accessibles qui generent du trafic qualifie.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Listez vos sujets** — Quels problemes votre produit resout ? Quelles questions se posent vos prospects ?\n2. **Utilisez des outils** — Google Keyword Planner, Ubersuggest, Ahrefs, SEMrush\n3. **Analysez la concurrence** — Quels mots-cles vos concurrents ciblent-ils ?\n4. **Privilegiez la longue traine** — Les requetes de 3-5 mots sont plus faciles a cibler et plus qualifiees\n5. **Creez un mapping** — Associez un mot-cle principal a chaque article prevu",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Lorsque vous creez un article dans le **Blog Manager**, pensez a definir votre mot-cle cible avant de rediger. L'IA de la **Content Factory** peut vous aider a generer des idees d'articles optimises autour de vos thematiques.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
  {
    slug: 'on-page-seo',
    title: 'SEO on-page (balises, structure, contenu)',
    duration: '4 min',
    glossaryTerms: ['seo', 'slug', 'excerpt'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le SEO on-page regroupe toutes les optimisations que vous pouvez faire directement sur vos pages. Ce sont les signaux que Google utilise pour comprendre de quoi parle votre contenu.\n\nLes elements cles :\n- **Meta Title** — Le titre affiche dans Google (50-60 caracteres)\n- **Meta Description** — Le resume sous le titre (150-160 caracteres)\n- **Slug** — L'URL lisible (ex: /blog/guide-seo-debutant)\n- **Structure Hn** — H1 unique, H2 pour les sections, H3 pour les sous-sections\n- **Contenu** — Texte riche, 1 500+ mots pour les articles piliers",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le meta title est le premier element que les utilisateurs voient dans les resultats de recherche. Un bon title peut doubler votre taux de clic (CTR). La meta description, bien qu'elle n'influence pas directement le classement, impacte fortement le CTR.\n\nUne structure de contenu claire (H1, H2, H3) aide Google a comprendre la hierarchie de votre article et peut vous faire apparaitre en \"featured snippet\" (position zero).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Meta Title** — Incluez le mot-cle cible au debut, ajoutez un benefice (ex: \"Guide SEO : 5 techniques pour doubler votre trafic\")\n2. **Meta Description** — Resumez la valeur de l'article et ajoutez un appel a l'action\n3. **Slug** — Court, lisible, avec le mot-cle (pas de caracteres speciaux)\n4. **Structure** — Un seul H1, des H2 pour chaque section majeure, des H3 si besoin\n5. **Maillage interne** — Liez vos articles entre eux pour creer un cocon semantique",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Blog Manager** vous fournit des champs dedies pour chaque element SEO on-page : meta title, meta description, slug et excerpt. L'editeur de contenu supporte les titres H2/H3 pour structurer vos articles.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
  {
    slug: 'technical-seo',
    title: 'SEO technique (vitesse, sitemap, JSON-LD)',
    duration: '3 min',
    glossaryTerms: ['seo', 'blog-manager'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le SEO technique concerne l'infrastructure de votre site : sa vitesse, son accessibilite par les robots de Google, et les donnees structurees qui aident les moteurs a comprendre votre contenu.\n\nLes elements techniques essentiels :\n- **Core Web Vitals** — Metriques de performance (LCP, FID, CLS) mesurees par Google\n- **Sitemap XML** — Plan du site qui guide les robots d'indexation\n- **JSON-LD** — Schema de donnees structurees (articles, FAQ, produits)\n- **HTTPS** — Certificat SSL obligatoire\n- **Mobile-first** — Google indexe d'abord la version mobile",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Depuis 2021, les Core Web Vitals sont un facteur de classement officiel chez Google. Un site lent perd des positions, quel que soit la qualite de son contenu. Les donnees structurees (JSON-LD) augmentent la visibilite dans les resultats de recherche avec des \"rich snippets\" (etoiles, FAQ, images).\n\nUn site qui passe de 3 a 1 seconde de chargement peut voir son taux de rebond chuter de 32%.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Mesurez vos Core Web Vitals** — Utilisez Google PageSpeed Insights ou Lighthouse\n2. **Optimisez les images** — Formats WebP, lazy loading, dimensions correctes\n3. **Generez un sitemap** — Soumettez-le dans Google Search Console\n4. **Ajoutez le JSON-LD** — Schema Article pour chaque article de blog\n5. **Verifiez le mobile** — Testez avec le Mobile-Friendly Test de Google",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Les articles publies via le **Blog Manager** generent automatiquement les balises JSON-LD (schema Article) et sont optimises pour le chargement rapide. Le sitemap est genere automatiquement pour l'ensemble de vos contenus publies.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
  {
    slug: 'content-seo',
    title: 'Rediger pour le SEO (et les humains)',
    duration: '4 min',
    glossaryTerms: ['seo', 'excerpt', 'featured-image'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Ecrire pour le SEO ne signifie pas bourrer un article de mots-cles. Depuis les mises a jour Google de 2022-2023 (Helpful Content Update), la qualite du contenu prime sur tout le reste. Un bon article SEO repond a une question reelle de votre audience, de maniere complete et accessible.\n\nLa regle d'or : ecrivez d'abord pour les humains, optimisez ensuite pour Google.\n\nUn contenu utile, original et bien structure sera naturellement bien positionne.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les articles de plus de 2 000 mots generent 3x plus de trafic et 3.5x plus de backlinks que les articles courts (SEMrush). Mais la longueur seule ne suffit pas : un article doit etre exhaustif sur son sujet.\n\nL'ajout d'un excerpt (resume) et d'une featured image (image a la une) ameliore le taux de clic dans les resultats de recherche et sur les reseaux sociaux.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Commencez par l'intention de recherche** — Que veut reellement savoir l'internaute ?\n2. **Structurez avec des sous-titres** — Un H2 par question ou angle majeur\n3. **Incluez des elements visuels** — Images, schemas, tableaux, listes a puces\n4. **Redigez un excerpt percutant** — 2-3 phrases qui donnent envie de lire\n5. **Choisissez une image a la une** — Pertinente, de qualite, avec un alt text descriptif\n6. **Relisez a voix haute** — Si ca sonne naturel, c'est bon pour le SEO",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** peut generer un premier jet d'article SEO a partir d'un brief. Affinez-le dans le **Blog Manager** en ajoutant votre expertise, un excerpt accrocheur et une image a la une. L'IA enrichit le contenu grace a votre Knowledge Base pour un rendu authentique.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
  {
    slug: 'your-blog-manager',
    title: 'Blog Manager & outils SEO dans FO',
    duration: '3 min',
    glossaryTerms: ['blog-manager', 'seo', 'slug', 'excerpt', 'featured-image'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le Blog Manager de Feedback Orchestrator est votre outil de publication SEO. Il reunit tout ce dont vous avez besoin pour creer, optimiser et publier des articles de blog performants : editeur riche, champs SEO, gestion des images et auto-traduction.",
      },
      {
        type: 'methodology',
        title: "Comment l'utiliser ?",
        content:
          "1. **Creez un article** — Renseignez le titre, le contenu, et le mot-cle cible\n2. **Optimisez le SEO** — Remplissez le meta title, la meta description et le slug\n3. **Ajoutez l'image a la une** — Choisissez une image pertinente avec un alt text\n4. **Redigez l'excerpt** — 2-3 phrases qui resument l'article pour les listes et les partages\n5. **Previsualiser et publier** — Verifiez le rendu avant de mettre en ligne\n6. **Suivez les performances** — Consultez le Marketing Dashboard pour voir le trafic genere",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur **Blog Manager** pour creer votre premier article. Chaque champ est documente avec des tooltips et des exemples. Combinez-le avec la **Content Factory** pour generer des premiers jets, et le **Content Calendar** pour planifier votre rythme de publication.",
      },
    ],
    linkedModule: {
      label: 'Blog Manager',
      path: '/growth/blog-manager',
      icon: 'Search',
    },
  },
];
